import React, { memo } from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from 'src/makeStyles';

import Grid from '@mui/material/Grid';
import { get } from 'lodash';
import { useTranslation } from '../../src/i18n';
import { colors, fontSize } from '../../constants/styleConstants';
import { TP, PRODUCTION_PARTNER_TYPE } from '../../constants/consts';
import CustomChips from '../CustomChips';
import {
  getChipsForProductionCard,
  getRoutePartWithSlug,
  getRouteWithSlug,
  getChipsForPerformanceCard,
} from '../../utils/common';
import route from '../../constants/routes';

const useStyles = makeStyles()(theme => ({
  root: {
    display: 'inline-flex',
    flexWrap: 'wrap',
    '& > *': {
      marginRight: theme.spacing(0.5),
    },
  },
  chip: {
    fontSize: fontSize.regularTitle,
    color: colors.white,
  },
}));

const PerformanceChips = ({
  className,
  production,
  type = 'small',
  chipClassName,
  isCoProduced,
  coProductions,
  festivals,
  checkPermissions,
  isDigitalOnly,
  viewType = 'production',
}) => {
  const { classes, cx } = useStyles();
  const { t } = useTranslation();
  const [
    isWorldPremier,
    isNationalPremier,
    isConcert,
    isNewProduction,
    isCancelled,
    isPartOfFestival,
    doubleTripleBill,
    isRectical,
  ] = viewType === 'production' ? getChipsForProductionCard(production) : getChipsForPerformanceCard(production);
  if (
    !isWorldPremier &&
    !isNationalPremier &&
    !isNewProduction &&
    !isPartOfFestival &&
    !isRectical &&
    !doubleTripleBill &&
    !isCoProduced &&
    !isConcert &&
    !isDigitalOnly
  ) {
    return null;
  }
  const hostListCompanies = production?.companies?.filter(item => item?.partnerType === PRODUCTION_PARTNER_TYPE.HOST);
  const hostListFestivals = production?.festivals?.filter(item => item?.partnerType === PRODUCTION_PARTNER_TYPE.HOST);
  return (
    <Grid item className={cx(classes.root, className)}>
      {isWorldPremier !== false && (
        <CustomChips
          type={type}
          size="small"
          label="WP"
          content={t(`${TP}.FN_PERFORMACE_TYPE_WORLD_PREMIERE`)}
          className={cx(classes.chip, chipClassName)}
        />
      )}
      {isNationalPremier && (
        <CustomChips
          type={type}
          size="small"
          label="NP"
          content={t(`${TP}.FN_PERFORMACE_TYPE_NATIONAL_PREMIERE`)}
          className={cx(classes.chip, chipClassName)}
        />
      )}
      {isNewProduction && (
        <CustomChips
          type={type}
          size="small"
          label="N"
          content={t(`${TP}.FN_PERFORMACE_TYPE_NEW_PRODUCTION`)}
          className={cx(classes.chip, chipClassName)}
        />
      )}
      {hostListCompanies?.length > 0 &&
        hostListCompanies.map(host => (
          <CustomChips
            type={type}
            size="small"
            label="H"
            linkProps={{
              href: `${route.COMPANIES}/[country]/[id]/[[...action]]`,
              as: getRouteWithSlug(`${route.COMPANIES}/${get(host, 'country.slug')}`, host),
            }}
            linkName={type !== 'small' && host?.name}
            content={t(`${TP}.FN_HOSTED_BY`)}
            isArchived={host?.id === -1}
            checkPermissions={checkPermissions}
            className={cx(classes.chip, chipClassName)}
          />
        ))}
      {hostListFestivals?.length > 0 &&
        hostListFestivals.map(host => (
          <CustomChips
            type={type}
            size="small"
            label="H"
            linkProps={{
              href: `${route.FESTIVALS}/[country]/[id]/[[...action]]`,
              as: getRouteWithSlug(`${route.FESTIVALS}/${get(host, 'country.slug')}`, host),
            }}
            linkName={type !== 'small' && host?.name}
            content={t(`${TP}.FN_HOSTED_BY`)}
            isArchived={host?.id === -1}
            checkPermissions={checkPermissions}
            className={cx(classes.chip, chipClassName)}
          />
        ))}
      {isCoProduced &&
        (coProductions?.length > 0 ? (
          coProductions.map(company => (
            <CustomChips
              type={type}
              size="small"
              label="CP"
              linkProps={{
                href: `${route.COMPANIES}/[country]/[id]/[[...action]]`,
                as: getRouteWithSlug(`${route.COMPANIES}/${get(company, 'country.slug')}`, company),
              }}
              linkName={company?.name}
              content={t(`${TP}.FN_CO_PRODUCED`)}
              isArchived={company?.id === -1}
              checkPermissions={checkPermissions}
              className={cx(classes.chip, chipClassName)}
            />
          ))
        ) : (
          <CustomChips
            type={type}
            size="small"
            label="CP"
            content={t(`${TP}.FN_CO_PRODUCED`)}
            className={cx(classes.chip, chipClassName)}
          />
        ))}
      {isPartOfFestival &&
        (festivals?.length > 0 ? (
          festivals.map(festival => (
            <CustomChips
              type={type}
              size="small"
              label="PF"
              linkProps={{
                href: `${route.FESTIVALS}/[country]/[id]/[[...action]]`,
                as: `${route.FESTIVALS}/${get(festival, 'country.slug')}/${getRoutePartWithSlug(festival)}`,
              }}
              isArchived={festival?.id === -1}
              linkName={festival?.name}
              checkPermissions={checkPermissions}
              content={t(`${TP}.FN_PERFORMACE_TYPE_PART_OF_FESTIVAL`)}
              className={cx(classes.chip, chipClassName)}
            />
          ))
        ) : (
          <CustomChips
            type={type}
            size="small"
            label="PF"
            content={t(`${TP}.FN_PERFORMACE_TYPE_PART_OF_FESTIVAL`)}
            className={cx(classes.chip, chipClassName)}
          />
        ))}
      {/* {liveEvent && (
        <CustomChips
        type={type}
          size="small"
          label="L"
          content={t(`${TP}.FN_PERFORMACE_TYPE_CONCERT`)}
          className={cx(classes.chip, classes.concert)}
        />
      )} */}
      {doubleTripleBill && (
        <CustomChips
          type={type}
          size="small"
          label="DB"
          content={t(`${TP}.FN_DOUBLE_TRIPLE_BILL`)}
          className={cx(classes.chip, chipClassName)}
        />
      )}
      {isConcert && (
        <CustomChips
          type={type}
          size="small"
          label="C"
          content={t(`${TP}.FN_PERFORMACE_TYPE_CONCERT`)}
          className={cx(classes.chip, chipClassName)}
        />
      )}
      {isRectical && (
        <CustomChips
          type={type}
          size="small"
          label="R"
          content={t(`${TP}.FN_RECITAL`)}
          className={cx(classes.chip, chipClassName)}
        />
      )}
      {isDigitalOnly && (
        <CustomChips
          type={type}
          size="small"
          label="D"
          content="Digital Only"
          // content={t(`${TP}.FN_PERFORMACE_TYPE_CONCERT`)}
          className={cx(classes.chip, chipClassName)}
        />
      )}
    </Grid>
  );
};

PerformanceChips.propTypes = {
  className: PropTypes.string,
  type: PropTypes.string,
  production: PropTypes.object,
};

export default memo(PerformanceChips);
